/**
 * The playout view's keyboard (D3, D25: keyboard-first): one pure reducer
 * from a key press — and where focus is — to what the operator meant.
 *
 *     Take                      F12, NumpadEnter; Enter or Space when focus is on nothing that takes keys itself
 *     Move next ±1              Shift+↓ / Shift+↑
 *     Select a part             ↓ / ↑ (a cursor over the running order, separate from Next)
 *     Set next                  N  (the selected part)
 *     Set next segment          Shift+N  (the selected part's segment, now)
 *     Queue next segment        Q  (the selected part's segment, after this one)
 *     Adlib / action            the studio's hotkeys (`adlibPanels[].items[].hotkey`, D22: `F1`, `Shift+F2`)
 *
 * Bindings are data: `DEFAULT_BINDINGS` is the built-in table; the show
 * style's keymap and the user's own layer replace an action's keys
 * (`keymap.ts` resolves the three into the table this reducer reads).
 *
 * **Double-take guard, first half:** a held key's auto-repeat never takes, and
 * a key typed into a text field or pressed on a control that handles it
 * itself (a button's Enter/Space) is not a hotkey. The second half — refusing
 * a take while one is in flight or within `minimumTakeSpan` — is `takeGuard`.
 */

import type { OperatorAction } from '@acp/engine/keymap.js';

/** Where the key press landed: a text field (typing), a control that handles Enter/Space itself, or neither. */
export type FocusKind = 'text' | 'control' | 'none';

export type KeyPress = {
  /** `KeyboardEvent.key`: `Enter`, ` `, `F12`, `n`, `ArrowDown`. */
  readonly key: string;
  /** `KeyboardEvent.code`: `NumpadEnter`, `KeyN`. */
  readonly code: string;
  readonly shift: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly meta: boolean;
  readonly repeat: boolean;
  readonly focus: FocusKind;
};

/** A key combination as written in bindings and the studio config: `F1`, `Shift+F2`, `Ctrl+Alt+K`, `NumpadEnter`, `Space`. */
export type Combo = { readonly key: string; readonly shift: boolean; readonly ctrl: boolean; readonly alt: boolean; readonly meta: boolean };

/** The operator actions (the show style's keymap names them: `@acp/engine/keymap.js`). */
export type HotkeyAction = OperatorAction;

/**
 * A binding. `anyFocus`: it fires even on a focused control (keys no control
 * uses — F12, NumpadEnter); otherwise only when focus is on nothing.
 */
export type Binding = { readonly combo: string; readonly action: HotkeyAction; readonly anyFocus: boolean };

export const DEFAULT_BINDINGS: readonly Binding[] = [
  { combo: 'F12', action: 'take', anyFocus: true },
  { combo: 'NumpadEnter', action: 'take', anyFocus: true },
  { combo: 'Enter', action: 'take', anyFocus: false },
  { combo: 'Space', action: 'take', anyFocus: false },
  { combo: 'Shift+ArrowDown', action: 'next-forward', anyFocus: true },
  { combo: 'Shift+ArrowUp', action: 'next-back', anyFocus: true },
  { combo: 'ArrowDown', action: 'select-down', anyFocus: false },
  { combo: 'ArrowUp', action: 'select-up', anyFocus: false },
  { combo: 'N', action: 'set-next', anyFocus: false },
  { combo: 'Shift+N', action: 'set-next-segment', anyFocus: false },
  { combo: 'Q', action: 'queue-segment', anyFocus: false },
];

const KEY_ALIASES: Readonly<Record<string, string>> = { space: ' ', spacebar: ' ', esc: 'Escape', return: 'Enter' };

/**
 * The named keys, by their lower-case spelling: a combo written `f9` or `arrowdown` names the key the browser
 * reports as `F9` / `ArrowDown` (`KeyboardEvent.key` is case-sensitive; the show style's keymap is not).
 */
const NAMED_KEYS: ReadonlyMap<string, string> = new Map(
  [
    ...Array.from({ length: 24 }, (_, i) => `F${String(i + 1)}`),
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Enter',
    'NumpadEnter',
    'Escape',
    'Tab',
    'Home',
    'End',
    'PageUp',
    'PageDown',
    'Backspace',
    'Delete',
    'Insert',
    'Pause',
  ].map((name) => [name.toLowerCase(), name]),
);

/** Parse `Shift+F2` into a combo; null when it names no key. Case-insensitive for modifiers and letters. */
export function parseCombo(text: string): Combo | null {
  const parts = text.split('+').map((p) => p.trim());
  const keyPart = parts.pop();
  if (keyPart === undefined || keyPart === '') return null;
  const mods = new Set(parts.map((p) => p.toLowerCase()));
  for (const m of mods) if (!['shift', 'ctrl', 'control', 'alt', 'option', 'meta', 'cmd'].includes(m)) return null;
  const alias = KEY_ALIASES[keyPart.toLowerCase()];
  return {
    key: alias ?? (keyPart.length === 1 ? keyPart.toLowerCase() : (NAMED_KEYS.get(keyPart.toLowerCase()) ?? keyPart)),
    shift: mods.has('shift'),
    ctrl: mods.has('ctrl') || mods.has('control'),
    alt: mods.has('alt') || mods.has('option'),
    meta: mods.has('meta') || mods.has('cmd'),
  };
}

/** Whether a press is exactly this combo (the key by `key`, or by `code` for the numpad and other code-only names). */
export function matches(combo: Combo, press: KeyPress): boolean {
  if (combo.shift !== press.shift || combo.ctrl !== press.ctrl || combo.alt !== press.alt || combo.meta !== press.meta) return false;
  const key = press.key.length === 1 ? press.key.toLowerCase() : press.key;
  // NumpadEnter's `key` is `Enter`: a combo naming the code must not also fire on the main Enter, nor the reverse.
  if (combo.key === 'NumpadEnter') return press.code === 'NumpadEnter';
  if (combo.key === 'Enter') return key === 'Enter' && press.code !== 'NumpadEnter';
  return key === combo.key;
}

/** One adlib panel item with a hotkey: a piece type fired, or an action run. */
export type HotkeyTarget = { readonly panelId: string; readonly index: number };

export type HotkeyContext = {
  readonly bindings: readonly Binding[];
  /** The studio config's item hotkeys, as written. */
  readonly items: readonly { readonly hotkey: string; readonly target: HotkeyTarget }[];
  /** Every part in running order. */
  readonly partOrder: readonly string[];
  readonly segmentOf: ReadonlyMap<string, string>;
  /** Where the part cursor starts when nothing is selected: the next part, else the on-air one. */
  readonly anchorPartId: string | null;
};

/** What a key press means. */
export type Intent =
  | { readonly type: 'take' }
  | { readonly type: 'moveNext'; readonly delta: 1 | -1 }
  | { readonly type: 'select'; readonly partId: string }
  | { readonly type: 'setNext'; readonly partId: string }
  | { readonly type: 'setNextSegment'; readonly segmentId: string }
  | { readonly type: 'queueNextSegment'; readonly segmentId: string }
  | { readonly type: 'item'; readonly target: HotkeyTarget };

/** The reducer's answer: what to do (null: nothing — let the browser have the key). */
export type HotkeyResult = { readonly intent: Intent | null };

function stepSelection(ctx: HotkeyContext, selected: string | null, delta: 1 | -1): Intent | null {
  const from = selected ?? ctx.anchorPartId;
  if (ctx.partOrder.length === 0) return null;
  const at = from === null ? -1 : ctx.partOrder.indexOf(from);
  const index = at < 0 ? (delta > 0 ? 0 : ctx.partOrder.length - 1) : Math.min(ctx.partOrder.length - 1, Math.max(0, at + delta));
  const partId = ctx.partOrder[index];
  return partId === undefined ? null : { type: 'select', partId };
}

function actionIntent(action: HotkeyAction, ctx: HotkeyContext, selected: string | null): Intent | null {
  const target = selected ?? ctx.anchorPartId;
  const segmentId = target === null ? undefined : ctx.segmentOf.get(target);
  switch (action) {
    case 'take':
      return { type: 'take' };
    case 'next-forward':
      return { type: 'moveNext', delta: 1 };
    case 'next-back':
      return { type: 'moveNext', delta: -1 };
    case 'select-down':
      return stepSelection(ctx, selected, 1);
    case 'select-up':
      return stepSelection(ctx, selected, -1);
    case 'set-next':
      return selected === null ? null : { type: 'setNext', partId: selected };
    case 'set-next-segment':
      return segmentId === undefined ? null : { type: 'setNextSegment', segmentId };
    case 'queue-segment':
      return segmentId === undefined ? null : { type: 'queueNextSegment', segmentId };
  }
}

/**
 * What a key press means, given the part the cursor is on. Typing into a text
 * field is never a hotkey; auto-repeat never is either (a held TAKE key must
 * not take twice).
 */
export function resolveHotkey(press: KeyPress, ctx: HotkeyContext, selected: string | null): HotkeyResult {
  if (press.focus === 'text' || press.repeat) return { intent: null };
  for (const binding of ctx.bindings) {
    const combo = parseCombo(binding.combo);
    if (combo === null || !matches(combo, press)) continue;
    if (!binding.anyFocus && press.focus !== 'none') continue;
    return { intent: actionIntent(binding.action, ctx, selected) };
  }
  for (const item of ctx.items) {
    const combo = parseCombo(item.hotkey);
    if (combo !== null && matches(combo, press)) return { intent: { type: 'item', target: item.target } };
  }
  return { intent: null };
}

// ─── the double-take guard's second half ───

export type TakeGuardInput = {
  /** The playlist is activated. */
  readonly active: boolean;
  readonly hasNext: boolean;
  /** A take is in flight (sent, unanswered). */
  readonly pending: boolean;
  /** The last take (the production's clock), and the production's clock now as estimated. */
  readonly lastTakeTime: number | null;
  readonly productionNowMs: number;
  /** The studio's `settings.minimumTakeSpanMs` (PLAN §5.3). */
  readonly minimumTakeSpanMs: number;
  /** The user may take (`playout.take`). */
  readonly permitted: boolean;
};

export type TakeGuard =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly reason: 'forbidden' | 'inactive' | 'no-next' | 'pending' | 'too-soon'; readonly retryInMs: number | null };

/**
 * Whether a take may be sent now. The production is the authority (it nacks
 * `take-too-soon` itself); this keeps a second press — or two keys pressed
 * together — from sending a second take the operator will only see refused.
 */
export function takeGuard(input: TakeGuardInput): TakeGuard {
  if (!input.permitted) return { allowed: false, reason: 'forbidden', retryInMs: null };
  if (!input.active) return { allowed: false, reason: 'inactive', retryInMs: null };
  if (!input.hasNext) return { allowed: false, reason: 'no-next', retryInMs: null };
  if (input.pending) return { allowed: false, reason: 'pending', retryInMs: null };
  if (input.lastTakeTime !== null) {
    const wait = input.lastTakeTime + input.minimumTakeSpanMs - input.productionNowMs;
    if (wait > 0) return { allowed: false, reason: 'too-soon', retryInMs: wait };
  }
  return { allowed: true };
}

/** What the TAKE button shows (`components/playout/TakeButton.tsx`'s states), from the guard and the last take's answer. */
export type TakeView = { readonly kind: 'ready' } | { readonly kind: 'pending' } | { readonly kind: 'blocked'; readonly reason: string } | { readonly kind: 'refused'; readonly reason: string };

const BLOCKED_TEXT: Readonly<Record<Exclude<TakeGuard, { allowed: true }>['reason'], string>> = {
  forbidden: 'Taking needs playout.take',
  inactive: 'Activate the playlist to take',
  'no-next': 'Nothing is next',
  pending: 'Taking…',
  'too-soon': 'Too soon after the last take',
};

/**
 * The button's state. In flight wins; then a guard's refusal; then the last
 * take's nack (until the next take) — so an operator sees why the take they
 * just pressed did nothing.
 */
export function takeView(guard: TakeGuard, lastTake: { readonly accepted: boolean; readonly reason: string | null; readonly message: string | null } | null): TakeView {
  if (!guard.allowed) return guard.reason === 'pending' ? { kind: 'pending' } : { kind: 'blocked', reason: BLOCKED_TEXT[guard.reason] };
  if (lastTake !== null && !lastTake.accepted) {
    const detail = lastTake.message === null || lastTake.message === '' ? '' : ` — ${lastTake.message}`;
    return { kind: 'refused', reason: `${lastTake.reason ?? 'refused'}${detail}` };
  }
  return { kind: 'ready' };
}
